import {JsonObject} from "../../shared/json/json-object";
import {FormGen} from "../../shared/formgen";
import {JsonProperty} from "../../shared/json/json-property";
import {md5_uuid} from "../../shared/md5";
import {JSON_OBJECT} from "../../shared/json/helpers";
import {
  $KTM,
  $KTS,
  BaseListItem,
  BaseListItemsLoader,
  ColorValue,
  DisplayName,
  KeyMetadatas,
  KeyTextString
} from "../../shared/types";
import {getMemberAuth} from "../../shared/auth";
import {PatientProfile, PracticeData} from "@/libwyze-shared/types";
import {black, colorPastelBlue, colorPastelGreen, colorPastelYellow, gray} from "../../shared/colors";

export enum ContactChannel {
  UNKNOWN = "unknown",
  EMAIL = "email",
  CHAT = "chat",
  PATIENT = "patient",
  // TODO: Use the following when refactoring this to fyneapps.
  // SYSTEM_USER = "sysuser", // A user type known to the system, e.g. Patient, with its own id.
}

export type ContactChannelMetadata = {
  text: string,
  colorValue?: ColorValue,
}

export const CONTACT_CHANNEL_KTM = new KeyMetadatas<ContactChannelMetadata>([
  $KTM(ContactChannel.UNKNOWN, {text: "Unknown", colorValue: new ColorValue(black, gray)}),
  $KTM(ContactChannel.EMAIL, {text: "Email", colorValue: new ColorValue(black, colorPastelGreen)}),
  $KTM(ContactChannel.CHAT, {text: "Chat", colorValue: new ColorValue(black, colorPastelYellow)}),
  $KTM(ContactChannel.PATIENT, {text: "Patient", colorValue: new ColorValue(black, colorPastelBlue)}),
]);

// Notes:
// Contact.id is the same as memberId for system channels (e.g. PATIENT)
@JsonObject()
export class Contact extends BaseListItem {

  @JsonProperty()
  channel: ContactChannel = ContactChannel.UNKNOWN;

  patient: PatientProfile; // Set when channelType === PATIENT.

  @JsonProperty()
  patientId: string; // Must be set when channelType === PATIENT.

  @FormGen({name: "First name", type: "string", required: true})
  @JsonProperty()
  firstname: string;

  @FormGen({name: "Last name", type: "string"})
  @JsonProperty()
  lastname: string;

  @FormGen({name: "Email", type: "email"})
  @JsonProperty()
  email: string;

  @FormGen({
    name: "Phone Number",
    type: "phone_number",
    required: true,
  })
  @JsonProperty()
  phoneNumber: string;

  @FormGen({
    name: "Date of Birth",
    type: "datenum",
  })
  @JsonProperty()
  birthDate: number;

  @FormGen({name: "Notes", type: "text", size: "xsmall"})
  @JsonProperty()
  text: string;

  static createNew(): Contact {
    const contact = new Contact(md5_uuid(), getMemberAuth().getMemberId(), Date.now());
    return contact;
  }

  static createNewPatient(patient: PatientProfile): Contact {
    const contact = new Contact(patient.id, null, patient.createdAt);
    contact.channel = ContactChannel.PATIENT;
    contact.firstname = patient.basicInfo.firstname;
    contact.lastname = patient.basicInfo.lastname;
    contact.email = patient.basicInfo.email;
    contact.phoneNumber = patient.basicInfo.phoneNumber;
    contact.patientId = patient.id;
    contact.patient = patient;
    return contact;
  }

  constructor(id: string, creator: string, created: number) {
    super(id, creator, created);
  }
}

export class Contacts extends BaseListItemsLoader<Contact> {

  private static instance: Contacts;

  static getInstance() {
    if (!this.instance) {
      this.instance = new Contacts();
    }
    return this.instance;
  }

  constructor() {
    super({shared: true, scopeRoot: true});
  }

  protected basePath(): string {
    return "contacts";
  }

  protected async onAfterItemsLoaded(items: Contact[]): Promise<Contact[]> {
    items.push(...((await PracticeData.provider().patientProfiles.getOrLoadListItems()).map(patient => Contact.createNewPatient(patient))));
    return items;
  }

  protected deserializeItem(value: any): Contact {
    return JSON_OBJECT.deserializeObject(value, Contact);
  }

  protected serializeItem(item: Contact): any {
    return JSON_OBJECT.serializeObject(item);
  }

  protected sortOrder(item1: Contact, item2: Contact): number {
    return item2.createdAt - item1.createdAt;
  }
}

export const contactAutocompleteLoader = () => {
  const contacts = Contacts.getInstance();
  return new Promise<KeyTextString[]>((resolve, reject) => {
    contacts.getOrLoadListItems()
      .then(contacts => resolve(contacts.map(contact => $KTS(contact.id, DisplayName(contact.firstname, contact.lastname)))))
      .catch(reason => reject(reason));
  });
}